import React, { useEffect, useMemo, useState } from 'react';
import CaffeineFields, { caffeineDefaults } from './CaffeineFields';

const DEFAULT_PFAND_VALUE = '0,25 €';
const ZERO_PFAND_VALUE = '0,00 €';
const PFAND_OPTIONS = [
  { value: '0,08 €', label: '0,08 €', hint: 'meist Mehrweg-Glas' },
  { value: '0,15 €', label: '0,15 €', hint: 'oft Mehrweg-PET' },
  { value: '0,25 €', label: '0,25 €', hint: 'oft Einweg' },
  { value: ZERO_PFAND_VALUE, label: '0,00 € alkoholisch', hint: 'nur für Alkohol ohne Pfand', forceStatus: 'alkoholisch' },
];

export default function AddDrinkForm({
  defaultEan = '',
  apiUrl = '/wp-json/pfandpirat/v1/drinks',
  onCancel,
  onSuccess,
}) {
  const categoriesUrl = useMemo(() => {
    try {
      const u = new URL(apiUrl, window.location.origin);
      return `${u.origin}/wp-json/pfandpirat/v1/drink-categories`;
    } catch (e) {
      return '/wp-json/pfandpirat/v1/drink-categories';
    }
  }, [apiUrl]);

  const [form, setForm] = useState({
    ...caffeineDefaults,
    product_name: '',
    ean_code: defaultEan,
    volume: '',
    pfand_info: DEFAULT_PFAND_VALUE,
    alcohol_content: '',
    status: 'nicht alkoholisch',
    category_id: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadCategories() {
      setCategoriesLoading(true);
      setCategoriesError(null);

      try {
        const res = await fetch(categoriesUrl, {
          method: 'GET',
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error(`Kategorien konnten nicht geladen werden (${res.status}).`);
        }

        const data = await res.json();

        if (!active) return;

        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (!active) return;
        setCategories([]);
        setCategoriesError(err.message || 'Kategorien konnten nicht geladen werden.');
      } finally {
        if (active) {
          setCategoriesLoading(false);
        }
      }
    }

    loadCategories();

    return () => {
      active = false;
    };
  }, [categoriesUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === 'status' && value !== 'alkoholisch' && f.pfand_info === ZERO_PFAND_VALUE) {
        next.pfand_info = DEFAULT_PFAND_VALUE;
      }
      return next;
    });
  };

  const pickPfand = (val) => setForm((f) => ({
    ...f,
    pfand_info: val,
    status: val === ZERO_PFAND_VALUE ? 'alkoholisch' : f.status,
  }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    let volumeValue = form.volume;
    if (typeof volumeValue === 'string') {
      volumeValue = volumeValue.replace(',', '.');
    }

    if (
      volumeValue === '' ||
      isNaN(parseFloat(volumeValue)) ||
      parseFloat(volumeValue) <= 0
    ) {
      setSaving(false);
      setError('Bitte gib das Volumen des Getränks an (z. B. 0,33 oder 1,5 Liter).');
      return;
    }

    volumeValue = parseFloat(volumeValue);

    let alcoholContentValue = form.alcohol_content;
    if (typeof alcoholContentValue === 'string') {
      alcoholContentValue = alcoholContentValue.replace(',', '.');
    }
    alcoholContentValue =
      alcoholContentValue !== '' && alcoholContentValue !== null
        ? parseFloat(alcoholContentValue)
        : null;

    const categoryIdValue =
      form.category_id !== '' && !isNaN(parseInt(form.category_id, 10))
        ? parseInt(form.category_id, 10)
        : null;

    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window.pfandpiratData.nonce,
        },
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          volume: volumeValue,
          alcohol_content: alcoholContentValue,
          category_id: categoryIdValue,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          res.status === 409
            ? 'EAN existiert bereits.'
            : 'Fehler ' + res.status;
        throw new Error(data?.message || msg);
      }


      // Dein Backend liefert { local_success, drink, sync_success, ... }
      // Damit SearchView wieder ein normales Ergebnisobjekt bekommt,
      // geben wir bevorzugt drink zurück.
      onSuccess?.(data?.drink ?? data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <h3>Neues Getränk anlegen</h3>

      <label style={styles.label}>
        Name
        <input
          name="product_name"
          value={form.product_name}
          onChange={handleChange}
          required
        />
      </label>

      <label style={styles.label}>
        EAN-Code
        <input
          name="ean_code"
          value={form.ean_code}
          readOnly
          style={{ background: '#f2f2f2' }}
        />
      </label>

      <label style={styles.label}>
        Kategorie
        <select
          name="category_id"
          value={form.category_id}
          onChange={handleChange}
          disabled={categoriesLoading}
        >
          <option value="">
            {categoriesLoading ? 'Kategorien werden geladen…' : '— Bitte wählen —'}
          </option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
        {categoriesError && (
          <span style={{ color: 'red', fontSize: 13 }}>{categoriesError}</span>
        )}
      </label>

      <label style={styles.label}>
        Volumen (L)
        <input
          name="volume"
          value={form.volume}
          onChange={handleChange}
          placeholder="z. B. 1,5"
          inputMode="decimal"
        />
      </label>

      <div style={styles.label}>
        Pfand
        <div style={styles.pfandRow}>
          {PFAND_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => pickPfand(option.value)}
              title={option.hint}
              style={{
                ...styles.pfandBtn,
                background: form.pfand_info === option.value ? '#28a745' : '#e9e9e9',
                color: form.pfand_info === option.value ? '#fff' : '#000',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        <small style={styles.pfandHint}>0,00 € ist nur für alkoholische Getränke ohne Pfand gedacht, damit sie für den Alkoholrechner gepflegt werden können.</small>
      </div>

      <label style={styles.label}>
        Alkoholgehalt (%)
        <input
          name="alcohol_content"
          value={form.alcohol_content}
          onChange={handleChange}
          placeholder="0,00"
          inputMode="decimal"
        />
      </label>

      <label style={styles.label}>
        Status
        <select
          name="status"
          value={form.status}
          onChange={handleChange}
        >
          <option value="nicht alkoholisch">Nicht alkoholisch</option>
          <option value="alkoholisch">Alkoholisch</option>
        </select>
      </label>

      <CaffeineFields form={form} setForm={setForm} />
      {error && <p role="alert" style={{ color: 'red', margin: 0 }}>{error}</p>}

      <div style={styles.buttonRow}>
        <button
          type="button"
          className="button button-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Abbrechen
        </button>
        <button
          type="submit"
          className="button button-primary"
          disabled={saving}
        >
          {saving ? 'Speichere…' : 'Anlegen'}
        </button>
      </div>
    </form>
  );
}

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    border: '1px solid #ddd',
    borderRadius: 6,
    background: '#fff',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 14,
  },
  pfandRow: {
    display: 'flex',
    gap: 8,
  },
  pfandBtn: {
    flex: '1 1 140px',
    padding: '6px 10px',
    border: '1px solid #ccc',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
  },
  pfandHint: {
    color: '#666',
    lineHeight: 1.4,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 4,
  },
};
